import { OrderPaymentStatus, PickupStatus } from '@leid/api-orders'
import { min } from 'date-fns'
import max from 'date-fns/max'

import { OrderInfo, OrderItemInfo } from 'appTypes/orders'

export const isCartOrder = (orderInfo: OrderInfo): boolean => {
  if (orderInfo.reconciliations.length !== 0) {
    return false
  }

  return orderInfo.orderItems.every(
    (item) => item.orderItem.status === OrderPaymentStatus.started && item.prolongations.length === 0,
  )
}

export const hasProcessingPayment = (orderInfo: OrderInfo): boolean =>
  orderInfo.orderItems.some((item) => item.orderItem.status === OrderPaymentStatus.paymentStarted) ||
  orderInfo.reconciliations.some(
    (item) =>
      item.reconciliationOrder.status !== OrderPaymentStatus.paid &&
      item.reconciliationOrder.status !== OrderPaymentStatus.canceled &&
      item.reconciliationOrder.status !== OrderPaymentStatus.paidAndCanceled,
  )

export const awaitsProlongationPayment = (orderInfo: OrderInfo): boolean => {
  if (
    orderInfo.order.status === OrderPaymentStatus.canceled ||
    orderInfo.order.status === OrderPaymentStatus.paidAndCanceled
  )
    return false

  const prolongations = orderInfo.orderItems
    .filter(
      (item) =>
        item.orderItem.status !== OrderPaymentStatus.canceled &&
        item.orderItem.status !== OrderPaymentStatus.paidAndCanceled,
    )
    .map((item) => item.prolongations || [])
    .flat()

  if (prolongations.length === 0) return false

  return prolongations.some((p) => !p.confirmed)
}

/**
 * This function sorts orders from newest to oldest
 * by the latest return date of any order item
 */
export const sortOrdersFromLatest = (orderInfos: OrderInfo[]): OrderInfo[] =>
  orderInfos.sort((a, b) => {
    const getReturnDates = (order: OrderInfo): Date[] =>
      order.orderItems
        .map((item) => (item.orderItem.rentedTo ? new Date(item.orderItem.rentedTo) : false))
        .filter((date): date is Date => date !== false)

    const aReturnDates = getReturnDates(a)
    const bReturnDates = getReturnDates(b)

    return max(bReturnDates).getTime() - max(aReturnDates).getTime()
  })

export const sortOrdersFromFirstToPick = (orderInfo: OrderInfo[]): OrderInfo[] => {
  return orderInfo.sort((a, b) => {
    const getPickupDates = (order: OrderInfo): Date[] =>
      order.orderItems
        .map((item) => (item.orderItem.rentedFrom ? new Date(item.orderItem.rentedFrom) : false))
        .filter((date): date is Date => date !== false)

    const aFromDates = getPickupDates(a)
    const bFromDates = getPickupDates(b)

    return max(aFromDates).getTime() - max(bFromDates).getTime()
  })
}

export const sortOrderItemsFromLatest = (orderItems: OrderItemInfo[]): OrderItemInfo[] =>
  orderItems.sort((a, b) => {
    const aReturnDate = a.orderItem.rentedTo
    const bReturnDate = b.orderItem.rentedTo

    if (!bReturnDate) {
      return -1
    }

    if (!aReturnDate) {
      return 1
    }

    return bReturnDate?.getTime() - aReturnDate?.getTime()
  })

export const sortOrderItemsFromFirstToPick = (orderItems: OrderItemInfo[]): OrderItemInfo[] => {
  return orderItems.sort((a, b) => {
    const aFromDate = a.orderItem.rentedFrom
    const bFromDate = b.orderItem.rentedFrom

    if (!bFromDate) {
      return -1
    }

    if (!aFromDate) {
      return 1
    }

    return aFromDate.getTime() - bFromDate.getTime()
  })
}

export const sortOrderItemsFromFirstToReturn = (orderItems: OrderItemInfo[]): OrderItemInfo[] => {
  return orderItems.sort((a, b) => {
    const aFromDate = a.orderItem.rentedTo
    const bFromDate = b.orderItem.rentedTo

    if (!bFromDate) {
      return -1
    }

    if (!aFromDate) {
      return 1
    }

    return aFromDate.getTime() - bFromDate.getTime()
  })
}

export const sortLocationOrderItemsGroupsFirstToPickup = (orderItemGroups: OrderItemInfo[][]): OrderItemInfo[][] => {
  return orderItemGroups.sort((a, b) => {
    const getDates = (group: OrderItemInfo[]): Date[] =>
      group
        .map((item) => {
          if (item.orderItem.pickupStatus === PickupStatus.EMPTY) {
            return item.orderItem.rentedFrom ?? false
          }

          if (item.orderItem.pickupStatus === PickupStatus.PICKEDUP) {
            return item.orderItem.rentedTo ?? false
          }

          return false
        })
        .filter((date): date is Date => date !== false)

    const aDates = getDates(a)
    const bDates = getDates(b)

    if (aDates.length && bDates.length) {
      if (min(aDates).getTime() > min(bDates).getTime()) {
        return 1
      }
      if (min(aDates).getTime() < min(bDates).getTime()) {
        return -1
      }

      return 0
    } else {
      if (aDates.length) {
        return -1
      }
      if (bDates.length) {
        return 1
      }

      return 0
    }
  })
}

export const filterActiveOrders = (orderInfos: OrderInfo[]): OrderInfo[] => {
  const now = new Date()

  const active = orderInfos.filter((s) => {
    const orderItemDates = s.orderItems
      .filter(
        (s) =>
          s.orderItem.status !== OrderPaymentStatus.canceled &&
          s.orderItem.status !== OrderPaymentStatus.paidAndCanceled,
      )
      .map((item) => (item.orderItem.rentedTo ? new Date(item.orderItem.rentedTo) : false))
    const activeOrderItems = orderItemDates.filter((s) => s !== false && s >= now)
    return activeOrderItems.length !== 0
  })
  return active
}

export const filterInactiveOrders = (allOrderInfoes: OrderInfo[], activeOrderInfoes: OrderInfo[]): OrderInfo[] => {
  const inactive = allOrderInfoes.filter((s) => {
    const inactiveOrder = activeOrderInfoes.find((a) => a.order.id === s.order.id)
    return inactiveOrder === undefined
  })
  return inactive
}
